<template>
  <div>
    <!-- MAIN EDITOR MODAL -->
    <div v-show="isOpen" class="role-editor-overlay" @mousedown.self="close">
      <div v-if="currentView === 'main'" class="role-editor-modal">
        <div class="modal-header">
          <h2 class="modal-title">
            {{ mode === 'CREATE' ? $t('orgDashboard.userDashboard.evaluation.addnewrole') : $t('orgDashboard.userDashboard.evaluation.editrole') }}
          </h2>
          <div class="header-right">
            
            <!-- ✨ NEW: DEPARTMENT / GROUP SELECTOR -->
            <div class="level-selector" v-click-outside="() => showDeptDropdown = false">
                <button class="level-select-minimal group-select-btn" @click="showDeptDropdown = !showDeptDropdown">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right:4px;"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                    {{ form.department || 'Assign Group' }}
                </button>
                <span class="dropdown-caret">▼</span>
                <ul v-if="showDeptDropdown" class="level-dropdown-menu">
                    <li v-for="dept in orgDepartments" :key="dept.label" @click="selectDepartment(dept.label)">
                        {{ dept.label }}
                    </li>
                    <li @click="selectDepartment('')" style="color: #ef4444; font-style: italic;">Clear Group</li>
                </ul>
            </div>
            
            <!-- ORIGINAL KVDR BADGE -->
            <div class="kvdr-badge" :class="kvdrTag.toLowerCase()" @click="currentView = 'detection'">
              {{ kvdrTag }}
              <span class="badge-tooltip">{{ getTagDescription }}</span>
            </div>
            <button class="close-btn" @click="close">&times;</button>
          </div>
        </div>

        <div class="modal-body">
          <!-- Assigned Candidates -->
          <div v-if="mode === 'EDIT' && form.profiles && form.profiles.length > 0" class="assigned-candidates-section">
              <div class="candidate-avatars">
                  <!-- CHANGED: openInlineTalentCard -> triggerTalentCard -->
                  <div v-for="user in form.profiles" :key="user.id || user.userId" class="avatar-wrapper" @click="triggerTalentCard(user.id || user.userId)">
                    <img :src="getAvatarUrl(user)" class="candidate-avatar" />
                    <span class="tooltip">{{ user.name || user.firstName || 'User' }}</span>
                  </div>
              </div>
          </div>

          <!-- TWO-COLUMN LAYOUT -->
          <div class="main-layout-container">
              <!-- LEFT COLUMN -->
              <div class="main-content-column">
                  
                  <!-- DESIGNATION / ROLE TITLE -->
                  <div class="title-level-wrapper">
                    <input type="text" v-model="form.title" :placeholder="$t('orgDashboard.userDashboard.evaluation.roleTitle')" class="designation-input minimal-title"/>
                    
                    <!-- CUSTOM LEVEL DROPDOWN -->
                    <div class="level-selector" v-click-outside="() => showLevelDropdown = false">
                        <button class="level-select-minimal" @click="showLevelDropdown = !showLevelDropdown">
                            {{ form.level || $t('orgDashboard.userDashboard.evaluation.selectlevel') }}
                        </button>
                        <span class="dropdown-caret">▼</span>
                        <ul v-if="showLevelDropdown" class="level-dropdown-menu">
                            <li v-for="lvl in orgLevels" :key="lvl.label || lvl" @click="selectLevel(lvl.label || lvl)">
                                {{ lvl.label || lvl }}
                            </li>
                        </ul>
                    </div>
                </div>

                <!-- MINIMAL DESCRIPTION / MISSION -->
                <div class="form-group mission-group">
                    <textarea v-model="form.mission" :placeholder="$t('orgDashboard.userDashboard.evaluation.purpose')" class="minimal-mission-input auto-expand-textarea" @input="resizeTextarea"></textarea>
                </div>

                  <!-- RESPONSIBILITIES -->
                  <div class="form-group">
                      <div class="label-row-with-ai">
                          <label>{{ $t('orgDashboard.userDashboard.evaluation.responsibilities') }}</label>
                          <vs-tooltip :text="$t('orgDashboard.userDashboard.evaluation.generateroledescription')">
                              <button class="ai-icon-btn" @click.prevent="generateAIContent" :disabled="isGeneratingJD">
                                  <svg v-if="!isGeneratingJD" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
                                  <span v-else class="loading-spinner"></span>
                              </button>
                          </vs-tooltip>
                      </div>
                      <div class="rich-text-toolbar">
                          <button @mousedown.prevent="formatText('bold')" title="Bold"><b>B</b></button>
                          <button @mousedown.prevent="formatText('italic')" title="Italic"><i>I</i></button>
                          <button @mousedown.prevent="formatText('underline')" title="Underline"><u>U</u></button>
                          <button @mousedown.prevent="formatText('insertUnorderedList')" title="Bullet List">•</button>
                          <select @mousedown.prevent @change="formatText('fontSize', $event.target.value)" title="Font Size">
                              <option value="3">Normal</option><option value="4">Large</option><option value="5">Huge</option>
                          </select>
                      </div>
                      <div class="rich-text-editor invisible-scroll-y" contenteditable="true" @blur="updateRichText('responsibilities', $event)" v-html="form.responsibilities"></div>
                  </div>

                  <!-- MISSION (JTBD) -->
                <div class="minimal-goals-section">
                    <label class="box-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="4"></circle></svg> 
                        {{ $t('orgDashboard.userDashboard.evaluation.mission') }}
                    </label>
                    
                    <div class="form-group">
                        <input type="text" v-model="form.primaryGoal" :placeholder="$t('orgDashboard.userDashboard.evaluation.primarygoal')" class="minimal-underline-input"/>
                    </div>
                    <div class="form-group">
                        <input type="text" v-model="form.secondaryGoal" :placeholder="$t('orgDashboard.userDashboard.evaluation.secondarygoal')" class="minimal-underline-input"/>
                    </div>
                </div>
              </div>

              <!-- RIGHT COLUMN (Absolute Scrollable Skills) -->
            <div class="skills-column">
                
                <div class="form-group">
                    <div class="search-wrapper minimal-input-wrapper">
                        <input type="text" v-model="funcSkillSearch" :placeholder="$t('orgDashboard.userDashboard.evaluation.functionalSkills')" class="minimal-input" @keydown.enter.prevent="addSkillsFromInput('functional')" @focus="showFuncDropdown = true" @blur="showFuncDropdown = false" />
                        <span class="dropdown-caret">▼</span>
                        
                        <ul v-show="showFuncDropdown" class="side-dropdown-list invisible-scroll-y">
                            <li v-for="skill in filteredFuncSkills" :key="skill" @mousedown.prevent="addSkill('functional', skill)">
                                {{ skill }} <span v-if="form.skills.includes(skill)" class="check-icon">✓</span>
                            </li>
                            <li v-if="filteredFuncSkills.length === 0" class="no-results">No matches found</li>
                        </ul>
                    </div>

                    <div class="tags-container">
                        <span v-for="(skill, index) in form.skills" :key="'f-'+index" class="skill-tag">
                            {{ skill }} <span class="remove-skill" @click="removeSkill('functional', index)">&times;</span>
                        </span>
                    </div>
                </div>

                <div class="form-group">
                    <div class="search-wrapper minimal-input-wrapper">
                        <input type="text" v-model="softSkillSearch" :placeholder="$t('orgDashboard.userDashboard.evaluation.softSkills')" class="minimal-input" @keydown.enter.prevent="addSkillsFromInput('soft')" @focus="showSoftDropdown = true" @blur="showSoftDropdown = false" />
                        <span class="dropdown-caret">▼</span>
                        
                        <ul v-show="showSoftDropdown" class="side-dropdown-list invisible-scroll-y">
                            <li v-for="skill in filteredSoftSkills" :key="skill" @mousedown.prevent="addSkill('soft', skill)">
                                {{ skill }} <span v-if="form.softSkills.includes(skill)" class="check-icon">✓</span>
                            </li>
                            <li v-if="filteredSoftSkills.length === 0" class="no-results">No matches found</li>
                        </ul>
                    </div>

                    <div class="tags-container">
                        <span v-for="(skill, index) in form.softSkills" :key="'s-'+index" class="skill-tag">
                            {{ skill }} <span class="remove-skill" @click="removeSkill('soft', index)">&times;</span>
                        </span>
                    </div>
                </div>

            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="openJDPreview">{{ $t('orgDashboard.userDashboard.evaluation.previewanddownload') }}</button>
          <button class="btn-primary" @click="saveRole">
            {{ mode === 'CREATE' ? $t('orgDashboard.userDashboard.evaluation.createrole') : $t('orgDashboard.userDashboard.evaluation.saveChanges') }}
          </button>
        </div>
      </div>

      <div v-if="currentView === 'detection'" class="role-editor-modal detection-modal">
          <div class="modal-header">
              <h2 class="modal-title">{{ $t('orgDashboard.userDashboard.evaluation.kvdrdetection') }}</h2>
              
              <!-- NEW: Header actions with Advanced Filter Icons -->
              <div class="header-actions">
                  <button class="action-icon-btn" @click="openAdvancedSettings" title="Advanced KVDR Settings">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>
                  </button>
                  <button class="close-btn" @click="currentView = 'main'">&times;</button>
              </div>
          </div>
          <div class="modal-body">
    <!-- NEW CLASSIFICATION BOX -->
    <div class="classification-box" :class="kvdrTag.toLowerCase()">
        <div class="class-left">
            <div class="class-label">{{ $t('orgDashboard.userDashboard.evaluation.currentclassification') }}</div>
            <div class="class-val">{{ kvdrTag }}</div>
            <div class="class-desc">{{ getTagDescription }}</div>
        </div>
        <div class="class-right">
            <div class="class-label">{{ $t('orgDashboard.userDashboard.evaluation.influence') }}</div>
            <div class="class-score"><span>{{ form.kvdr.influenceScore }}</span>/5</div>
        </div>
    </div>

    <!-- NEW VALUE DRIVER BUTTONS -->
    <div class="form-group">
        <label>{{ $t('orgDashboard.userDashboard.evaluation.whatdrivesthisrole') }}</label>
        <div class="btn-group">
            <button :class="{'active': form.kvdr.valueDriver === 'Person'}" @click="form.kvdr.valueDriver = 'Person'">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {{ $t('orgDashboard.userDashboard.evaluation.person') }}
            </button>
            <button :class="{'active': form.kvdr.valueDriver === 'Role'}" @click="form.kvdr.valueDriver = 'Role'">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22v-5"/><path d="M16 17H8"/><path d="M20 17H4"/><path d="m4 12 8-10 8 10"/><path d="M2 12h20"/></svg>
                {{ $t('orgDashboard.userDashboard.evaluation.role') }}
            </button>
        </div>
    </div>

    <!-- NEW VALUE KIND PILLS -->
    <div class="form-group">
        <label>{{ $t('orgDashboard.userDashboard.evaluation.whatkidnofvalue') }}</label>
        <div class="pill-group">
            <button v-for="tag in ['Revenue', 'Cost', 'Risk', 'Ops', 'Brand', 'Social', 'Research', 'IP']" :key="tag"
                    class="pill" :class="{'active': form.kvdr.valueTags.includes(tag)}"
                    @click="toggleArrayItem(form.kvdr.valueTags, tag)">
                {{ $t('orgDashboard.userDashboard.evaluation.' + tag.toLowerCase()) }}
            </button>
            <button class="pill" :class="{'active': form.kvdr.valueTags.includes('Other')}"
                    @click="toggleArrayItem(form.kvdr.valueTags, 'Other')">
                {{ $t('orgDashboard.userDashboard.evaluation.other') }}
            </button>
        </div>
        <!-- Note: Placeholder for input is kept manual because it is a typed response -->
        <input v-if="form.kvdr.valueTags.includes('Other')" type="text" v-model="form.kvdr.otherValueText" placeholder="Specify other value..." class="form-control other-value-input"/>
    </div>

    <!-- NEW BUSINESS TERMS CHECKBOXES -->
    <div class="form-group">
        <label>{{ $t('orgDashboard.userDashboard.evaluation.canthisvalue') }}</label>
        <div class="grid-2">
            <label class="checkbox-button" v-for="expr in ['Revenue generated', 'Cost saved', 'Time reduced', 'Risk mitigated', 'Quality improved', 'Capability built']" :key="expr">
                <input type="checkbox" :value="expr" v-model="form.kvdr.businessExpressions">
                <div class="checkbox-content">
                    <span class="custom-check">
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3337 4L6.00033 11.3333L2.66699 8" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </span>
                    <!-- Map array strings to their respective translation keys -->
                    {{ expr === 'Revenue generated' ? $t('orgDashboard.userDashboard.evaluation.revenue') : $t('orgDashboard.userDashboard.evaluation.' + expr.replace(' ', '').toLowerCase()) }}
                </div>
            </label>
        </div>
    </div>

    <!-- NEW SLIDERS -->
    <div class="form-group">
        <label>{{ $t('orgDashboard.userDashboard.evaluation.valueinfluencescore') }} <span class="score-badge">{{ form.kvdr.influenceScore }}/5</span></label>
        <input type="range" min="1" max="5" v-model.number="form.kvdr.influenceScore" class="slider orange-slider" @input="updateSliderStyle">
        <div class="slider-labels">
            <span>{{ $t('orgDashboard.userDashboard.evaluation.noinfluence') }}</span>
            <span>{{ $t('orgDashboard.userDashboard.evaluation.directdriver') }}</span>
        </div>
    </div>

    <div class="form-group">
        <label>{{ $t('orgDashboard.userDashboard.evaluation.ownershipclarity') }} <span class="score-badge">{{ form.kvdr.ownershipClarity }}/5</span></label>
        <input type="range" min="1" max="5" v-model.number="form.kvdr.ownershipClarity" class="slider orange-slider" @input="updateSliderStyle">
        <div class="slider-labels">
            <span>{{ $t('orgDashboard.userDashboard.evaluation.diffuse') }}</span>
            <span>{{ $t('orgDashboard.userDashboard.evaluation.clear') }}</span>
        </div>
    </div>
</div>
        <div class="modal-footer">
            <button class="btn-secondary" @click="currentView = 'main'">{{ $t('orgDashboard.userDashboard.evaluation.back') }}</button>
            <button class="btn-primary" @click="saveDetectionMetrics" :disabled="isSavingKvdr">
                {{ isSavingKvdr ? 'Saving...' : $t('orgDashboard.userDashboard.evaluation.done') }}
            </button>
            </div>
      </div>
    </div>

    <!-- ATTRIBUTION LAYER VIEW -->
      <div v-if="currentView === 'attribution'" class="role-editor-modal detection-modal">
          <div class="modal-header">
              <h2 class="modal-title">Attribution Analysis</h2>
              <button class="close-btn" @click="currentView = 'detection'">&times;</button>
          </div>
          <div class="modal-body">
              <AttributionLayer 
                :kvdr="form.kvdr" 
                :kvdrTag="kvdrTag"
                :clarityScore="clarityScore"
                :attributionScore="attributionScore"
                :calcScoreBlock="calcScoreBlock"
              />
          </div>
          <div class="modal-footer">
              <button class="btn-primary" @click="currentView = 'detection'">Done</button>
          </div>
      </div>

      <!-- QUANTIFICATION LAYER VIEW -->
      <div v-if="currentView === 'quantification'" class="role-editor-modal detection-modal">
          <div class="modal-header">
              <h2 class="modal-title">Value Quantification</h2>
              <button class="close-btn" @click="currentView = 'detection'">&times;</button>
          </div>
          <div class="modal-body">
              <QuantificationLayer 
                :kvdr="form.kvdr"
                :currencies="currencies"
                :currentCurrencySymbol="currentCurrencySymbol"
                :formatNumber="formatNumber"
                :adjustedValue="adjustedValue"
                :rawValue="rawValue"
                :valueBand="valueBand"
              />
          </div>
          <div class="modal-footer">
              <button class="btn-primary" @click="currentView = 'detection'">Done</button>
          </div>
      </div>

    <!-- SEPARATE JD PREVIEW POPUP -->
    <div v-if="showJDModal" class="role-editor-overlay jd-preview-overlay" @mousedown.self="showJDModal = false">
      <div class="jd-preview-modal">
        <div class="modal-header">
            <h2 class="modal-title">Job Description Preview</h2>
            <button class="close-btn" @click="showJDModal = false">&times;</button>
        </div>
        
        <!-- Elegant Download Toolbar -->
        <div class="jd-toolbar">
            <span class="toolbar-label">Download As:</span>
            <button class="dl-btn" @click="downloadJD('txt')">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg> TXT
            </button>
            <button class="dl-btn" @click="downloadJD('doc')">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M9 15l2 2 4-4"></path></svg> Word
            </button>
            <button class="dl-btn" @click="downloadJD('pdf')">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><path d="M9 15v-4h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H9z"></path></svg> PDF
            </button>
        </div>

        <div class="modal-body jd-body">
            <div class="rich-text-editor readonly-jd" v-html="generatedJD"></div>
        </div>
      </div>
    </div>
  <!-- 🌟 NEW: UNIFIED ROLE VALUE ANALYSIS MODAL 🌟 -->
    <div v-if="showAdvancedModal" class="role-editor-overlay" style="z-index: 22000;" @mousedown.self="showAdvancedModal = false">
      <div class="unified-advanced-modal">
          
          <!-- LEFT: MAIN CONTENT AREA -->
          <div class="uam-main">
              <div class="uam-header">
                  <div>
                      <h2>Role Value Analysis</h2>
                      <p class="uam-subtitle">{{ form.title || 'Untitled Role' }}</p>
                  </div>
                  <button class="close-btn" @click="showAdvancedModal = false">&times;</button>
              </div>

              <!-- HORIZONTAL STEP TABS -->
              <div class="uam-tabs">
                  <button :class="{active: advancedTab === 'attribution'}" @click="changeStep('attribution')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg> Attribution
                  </button>
                  <span class="tab-divider">&gt;</span>
                  <button :class="{active: advancedTab === 'quantification'}" @click="changeStep('quantification')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"></rect><circle cx="12" cy="12" r="2"></circle><path d="M6 12h.01M18 12h.01"></path></svg> Quantification
                  </button>
                  <span class="tab-divider">&gt;</span>
                  <button :class="{active: advancedTab === 'compensation'}" @click="changeStep('compensation')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg> Compensation
                  </button>
                  <span class="tab-divider">&gt;</span>
                  <button :class="{active: advancedTab === 'agentic'}" @click="changeStep('agentic')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg> Agentic
                  </button>
              </div>

              <!-- Content Area omitted for brevity (Keep it the same) -->

              

              <div class="uam-content-area invisible-scroll-y">
                  <AttributionLayer 
                    v-show="advancedTab === 'attribution'"
                    :kvdr="form.kvdr" 
                    :kvdrTag="kvdrTag"
                    :clarityScore="clarityScore"
                    :attributionScore="attributionScore"
                    :calcScoreBlock="calcScoreBlock"
                  />
                  <QuantificationLayer 
                    v-show="advancedTab === 'quantification'"
                    :kvdr="form.kvdr"
                    :currencies="currencies"
                    :currentCurrencySymbol="currentCurrencySymbol"
                    :formatNumber="formatNumber"
                    :adjustedValue="adjustedValue"
                    :rawValue="rawValue"
                    :valueBand="valueBand"
                  />
                  <CompensationLayer 
                    v-show="advancedTab === 'compensation'"
                    :kvdr="form.kvdr"
                    :currencies="currencies"
                    :currentCurrencySymbol="currentCurrencySymbol"
                    :formatNumber="formatNumber"
                    :adjustedValue="adjustedValue"
                    :totalEmploymentCost="totalEmploymentCost"
                    :vcRatio="vcRatio"
                    :alignmentBand="alignmentBand"
                    :compLogicRecommendation="compLogicRecommendation"
                    :totalScore="clarityScore + attributionScore"
                    :directContributionPct="form.kvdr.attribution.directContributionPct || 0"
                    :perksProxyCost="perksProxyCost"
                  />
                  <AgenticLayer 
                    v-show="advancedTab === 'agentic'"
                    :kvdr="form.kvdr"
                    :currentCurrencySymbol="currentCurrencySymbol"
                    :formatNumber="formatNumber"
                    :totalEmploymentCost="totalEmploymentCost"
                    :agentFitScore="agentFitScore"
                    :agenticRecommendation="agenticRecommendation"
                    :estimatedSaving="estimatedSaving"
                  />
                  <div v-show="advancedTab === 'reconcile'" class="reconcile-tab-view">
                      <div class="metric-card">
                          <div class="section-title"><span class="icon text-orange"></span> CROSS-LAYER RECONCILIATION</div>
                          
                          <div class="recon-main-list mt-4">
                              <!-- Row 1 -->
                              <div class="recon-main-row">
                                  <div class="rm-left">
                                      <span class="icon" :class="clarityScore > 0 ? 'text-yellow' : 'text-gray'">
                                          <!-- Star Icon -->
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                                      </span>
                                      <span class="rm-label">DETECTION</span>
                                  </div>
                                  <div class="rm-right">
                                      <strong class="rm-val">{{ kvdrTag }}</strong>
                                      <span class="rm-sub">Score {{ clarityScore + attributionScore }}/25</span>
                                  </div>
                              </div>
                              
                              <!-- Row 2 -->
                              <div class="recon-main-row">
                                  <div class="rm-left">
                                      <span class="icon" :class="attributionScore > 0 ? 'text-blue' : 'text-gray'">
                                          <!-- Layers Icon -->
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                                      </span>
                                      <span class="rm-label">ATTRIBUTION</span>
                                  </div>
                                  <div class="rm-right">
                                      <strong class="rm-val">{{ attributionScore < 12 ? 'LOW' : attributionScore < 20 ? 'MID' : 'HIGH' }}</strong>
                                      <span class="rm-sub">{{ form.kvdr.attribution.directContributionPct }}% direct</span>
                                  </div>
                              </div>

                              <!-- Row 3 -->
                              <div class="recon-main-row">
                                  <div class="rm-left">
                                      <span class="icon" :class="adjustedValue > 0 ? 'text-green' : 'text-gray'">
                                          <!-- Money/Bill Icon -->
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"></rect><circle cx="12" cy="12" r="2"></circle><path d="M6 12h.01M18 12h.01"></path></svg>
                                      </span>
                                      <span class="rm-label">QUANTIFICATION</span>
                                  </div>
                                  <div class="rm-right">
                                      <strong class="rm-val">{{ currentCurrencySymbol }}{{ formatNumber(adjustedValue) }}</strong>
                                      <span class="rm-sub">Band: {{ valueBand }}</span>
                                  </div>
                              </div>

                              <!-- Row 4 -->
                              <div class="recon-main-row">
                                  <div class="rm-left">
                                      <span class="icon" :class="totalEmploymentCost > 0 ? 'text-purple' : 'text-gray'">
                                          <!-- Dollar Icon -->
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                                      </span>
                                      <span class="rm-label">COMPENSATION</span>
                                  </div>
                                  <div class="rm-right">
                                      <strong class="rm-val">{{ alignmentBand }}</strong>
                                      <span class="rm-sub">Cost {{ currentCurrencySymbol }}{{ formatNumber(totalEmploymentCost) }}</span>
                                  </div>
                              </div>

                              <!-- Row 5 -->
                              <div class="recon-main-row" style="border-bottom: none; padding-bottom: 0;">
                                  <div class="rm-left">
                                      <span class="icon" :class="estimatedSaving > 0 ? 'text-cyan' : 'text-gray'">
                                          <!-- 🤖 NEW ROBOT ICON -->
                                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg>
                                      </span>
                                      <span class="rm-label">AGENTIC</span>
                                  </div>
                                  <div class="rm-right">
                                      <strong class="rm-val">{{ agenticRecommendation.action }}</strong>
                                  </div>
                              </div>
                          </div>

                          <!-- Status Box -->
                          <div class="recon-status-box success mt-16" style="margin-bottom: 0;">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                              <span>No cross-layer inconsistencies detected — the role profile is internally coherent.</span>
                          </div>
                      </div>
                  </div>
              </div>

              <div class="uam-footer">
                  <button class="btn-secondary" v-if="advancedTab === 'quantification'" @click="changeStep('attribution')">&lt; Back</button>
                  <button class="btn-secondary" v-if="advancedTab === 'compensation'" @click="changeStep('quantification')">&lt; Back</button>
                  <button class="btn-secondary" v-if="advancedTab === 'agentic'" @click="changeStep('compensation')">&lt; Back</button>
                  <button class="btn-secondary" v-if="advancedTab === 'reconcile'" @click="changeStep('agentic')">&lt; Back</button>
                  
                  <div style="flex:1"></div>
                  
                  <button class="btn-primary" v-if="advancedTab === 'attribution'" @click="changeStep('quantification')">Next: Quantification &gt;</button>
                  <button class="btn-primary" v-if="advancedTab === 'quantification'" @click="changeStep('compensation')">Next: Compensation &gt;</button>
                  <button class="btn-primary" v-if="advancedTab === 'compensation'" @click="changeStep('agentic')">Next: Agentic &gt;</button>
                  <button class="btn-primary" v-if="advancedTab === 'agentic'" @click="changeStep('reconcile')">Review & reconcile &gt;</button>
                  
                  <button class="btn-primary save-btn-wide" v-if="advancedTab === 'reconcile'" @click="saveAnalysis">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg> Save analysis
                  </button>
              </div>

          </div>

          <!-- RIGHT: LIVE RECONCILIATION SIDEBAR -->
          <div class="uam-sidebar">
              <h4 class="sidebar-title">LIVE RECONCILIATION</h4>
              
              <div class="recon-item">
                  <div class="recon-icon" :class="clarityScore > 0 ? 'text-yellow' : 'text-gray'">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                  </div>
                  <div class="recon-info">
                      <span>DETECTION</span>
                      <strong>{{ kvdrTag }}</strong>
                  </div>
                  <div class="recon-meta">Score {{ clarityScore + attributionScore }}/25</div>
              </div>

              <div class="recon-item">
                  <div class="recon-icon" :class="attributionScore > 0 ? 'text-blue' : 'text-gray'">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 2 7 12 12 22 7 12 2"></polygon><polyline points="2 17 12 22 22 17"></polyline><polyline points="2 12 12 17 22 12"></polyline></svg>
                  </div>
                  <div class="recon-info">
                      <span>ATTRIBUTION</span>
                      <strong>{{ attributionScore < 12 ? 'LOW' : attributionScore < 20 ? 'MID' : 'HIGH' }}</strong>
                  </div>
                  <div class="recon-meta">{{ form.kvdr.attribution.directContributionPct }}% direct</div>
              </div>

              <div class="recon-item">
                  <div class="recon-icon" :class="adjustedValue > 0 ? 'text-green' : 'text-gray'">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"></rect><circle cx="12" cy="12" r="2"></circle><path d="M6 12h.01M18 12h.01"></path></svg>
                  </div>
                  <div class="recon-info">
                      <span>QUANTIFICATION</span>
                      <strong>{{ currentCurrencySymbol }}{{ formatNumber(adjustedValue) }}</strong>
                  </div>
                  <div class="recon-meta">Band: {{ valueBand }}</div>
              </div>

              <div class="recon-item">
                  <div class="recon-icon" :class="totalEmploymentCost > 0 ? 'text-purple' : 'text-gray'">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                  </div>
                  <div class="recon-info">
                      <span>COMPENSATION</span>
                      <strong>{{ alignmentBand }}</strong>
                  </div>
                  <div class="recon-meta">Cost {{ currentCurrencySymbol }}{{ formatNumber(totalEmploymentCost) }}</div>
              </div>

              <div class="recon-item">
                  <div class="recon-icon" :class="estimatedSaving > 0 ? 'text-cyan' : 'text-gray'">
                      <!-- 🤖 NEW ROBOT ICON -->
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"></rect><circle cx="12" cy="5" r="2"></circle><path d="M12 7v4"></path><line x1="8" y1="16" x2="8" y2="16"></line><line x1="16" y1="16" x2="16" y2="16"></line></svg>
                  </div>
                  <div class="recon-info">
                      <span>AGENTIC</span>
                      <strong>{{ agenticRecommendation.action }}</strong>
                  </div>
              </div>

              <div class="recon-status-box success mt-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                  <span>No cross-layer inconsistencies detected — the role profile is internally coherent.</span>
              </div>
          </div>
      </div>
    </div>

  </div>
</template>

<script>
import allFunctionalSkills from '@/assets/data/all-skills.js';
import allSoftSkills from '@/assets/data/all-personality.js';
import AttributionLayer from './AttributionLayer.vue';
import QuantificationLayer from './QuantificationLayer.vue';
import CompensationLayer from './CompensationLayer.vue'; 
import AgenticLayer from './AgenticLayer.vue';
export default {
    name: 'RoleEditorModal',
    components: {
        // NEW: Register components
        AttributionLayer,
        QuantificationLayer,
        CompensationLayer,
        AgenticLayer 
    },
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    // Check that click was outside the el and its children
                    if (!(el === event.target || el.contains(event.target))) {
                        // Correctly evaluate inline functions or string method names
                        if (typeof binding.value === 'function') {
                            binding.value(event);
                        } else if (vnode.context && typeof vnode.context[binding.expression] === 'function') {
                            vnode.context[binding.expression](event);
                        }
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            },
        }
    },
    data() {
        return {
            isOpen: false, showLevelDropdown: false, currentView: 'main', showDeptDropdown: false,
            mode: 'CREATE', onSaveCallback: null, isGeneratingJD: false, orgDepartments: [], 
            generatedJD: '', isSavingKvdr: false, showJDModal: false, 
            funcSkillSearch: '', softSkillSearch: '', showFuncDropdown: false, showSoftDropdown: false,
            orgLevels: [], masterFuncSkills: allFunctionalSkills || [], masterSoftSkills: allSoftSkills || [],
            showAdvancedModal: false,
            advancedTab: 'attribution',
            // CURRENCIES FOR QUANTIFICATION
            currencies: [
                { id: 'USD', symbol: '$' }, { id: 'EUR', symbol: '€' },
                { id: 'GBP', symbol: '£' }, { id: 'INR', symbol: '₹' }
            ],

            form: {
                selfId: null, title: '', level: '', mission: '', responsibilities: '', department: '',
                primaryGoal: '', secondaryGoal: '', skills: [], softSkills: [], profiles: [],    
                kvdr: {
                    valueDriver: '', valueTags: [], businessExpressions: [], 
                    influenceScore: 1, ownershipClarity: 3, otherValueText: '',
                    
                    // LAYER 2: ATTRIBUTION DEFAULTS (Added leverageScore)
                    attribution: {
                        painSolved: '', impactFrequency: '', counterfactualLoss: '', measurable: '',
                        attributionStatus: '', directContributionPct: 50, underperformanceSpeed: '',
                        replaceability: [], downstreamDeps: '', flowPosition: '', decisionLevel: '',
                        outcomeOrientation: '', leverageScore: 0 
                    },
                    // LAYER 3: QUANTIFICATION DEFAULTS (Updated High threshold to 500k)
                    quantification: {
                        currency: 'USD', revenueInfluenced: null, costSaved: null, riskReduced: null,
                        cycleTimeValue: null, qualityValue: null, strategicValue: null, notes: '',
                        confidence: 50, lowMidThreshold: 50000, midHighThreshold: 500000
                    }
                }
            }
        }
    },
    computed: {
        filteredFuncSkills() {
            if(!this.funcSkillSearch) return this.masterFuncSkills.slice(0, 50);
            const searchTerms = this.funcSkillSearch.split(',').map(s => s.trim());
            const currentSearch = searchTerms[searchTerms.length - 1].toLowerCase();
            if (!currentSearch) return this.masterFuncSkills.slice(0, 50);
            return this.masterFuncSkills.filter(s => s && s.toLowerCase().includes(currentSearch)).slice(0, 50);
        },
        filteredSoftSkills() {
            if(!this.softSkillSearch) return this.masterSoftSkills;
            const searchTerms = this.softSkillSearch.split(',').map(s => s.trim());
            const currentSearch = searchTerms[searchTerms.length - 1].toLowerCase();
            if (!currentSearch) return this.masterSoftSkills;
            return this.masterSoftSkills.filter(s => s && s.toLowerCase().includes(currentSearch));
        },
        kvdrTag() {
            const inf = this.form.kvdr.influenceScore || 1;
            const bizLen = (this.form.kvdr.businessExpressions || []).length;
            if (inf >= 4 && bizLen >= 1) return 'KVDR';
            if (inf >= 2) return 'SVR';
            return 'NVR';
        },
        
        // CLARITY PILLAR (Layer 1 -> Layer 2 Link)
        clarityScore() {
            let score = 0;
            const k = this.form.kvdr;
            score += k.valueDriver ? 1 : 0;
            score += (k.valueTags || []).length >= 1 ? 1 : 0;
            score += (k.businessExpressions || []).length >= 1 ? 1 : 0;
            score += this.form.primaryGoal && this.form.primaryGoal.trim() !== '' ? 1 : 0;
            score += k.influenceScore >= 4 ? 0.5 : 0;
            score += k.ownershipClarity >= 4 ? 0.5 : 0;
            return Math.round(Math.min(5, score));
        },
        getTagDescription() {
            if (this.kvdrTag === 'KVDR') return this.$t('orgDashboard.userDashboard.evaluation.kvdr');
            if (this.kvdrTag === 'SVR') return this.$t('orgDashboard.userDashboard.evaluation.svr');
            return this.$t('orgDashboard.userDashboard.evaluation.nvr');
        },
        attributionScore() {
            return this.calcScoreBlock('criticality') + 
                   this.calcScoreBlock('value') + 
                   this.calcScoreBlock('risk') + 
                   this.calcScoreBlock('influence');
        },

        // SEED CONFIDENCE FOR LAYER 3
        attributionConfidenceAuto() {
            const a = this.form.kvdr.attribution;
            if (!a) return 50;
            // High confidence rule
            if (a.measurable === 'tangible' && a.attributionStatus === 'yes' && a.directContributionPct >= 70) return 85;
            // Low confidence rule
            if (a.measurable === 'no' || a.attributionStatus === 'no' || a.directContributionPct < 30) return 30;
            // Mixed signals
            return 60; 
        },

        // LAYER 3: QUANTIFICATION MATH
        currentCurrencySymbol() {
            const curr = this.currencies.find(c => c.id === this.form.kvdr.quantification?.currency);
            return curr ? curr.symbol : '$';
        },
        rawValue() {
            const q = this.form.kvdr.quantification;
            if (!q) return 0;
            return (q.revenueInfluenced || 0) + (q.costSaved || 0) + (q.riskReduced || 0) + 
                   (q.cycleTimeValue || 0) + (q.qualityValue || 0) + (q.strategicValue || 0);
        },
        adjustedValue() {
            const q = this.form.kvdr.quantification;
            if (!q) return 0;
            return Math.round(this.rawValue * ((q.confidence || 0) / 100));
        },
        valueBand() {
            const val = this.adjustedValue;
            const q = this.form.kvdr.quantification;
            if (!q) return 'LOW';
            if (val >= (q.midHighThreshold || 500000)) return 'HIGH';
            if (val >= (q.lowMidThreshold || 50000)) return 'MID';
            return 'LOW';
        },
        perksProxyCost() {
            const c = this.form.kvdr.compensation;
            if(!c) return 0;
            const dailyRate = (c.baseSalary || 0) / 220;
            return (c.interns || 0) * 25000 
                 + (c.assistant ? 35000 : 0) 
                 + (c.recognitionRewards ? 2000 : 0) 
                 + (c.learningBudget || 0) 
                 + ((c.extraTimeOffDays || 0) * dailyRate);
        },
        totalEmploymentCost() {
            const c = this.form.kvdr.compensation;
            if(!c) return 0;
            return ((c.baseSalary || 0) * (c.employmentCostMultiplier || 1.3))
                 + ((c.baseSalary || 0) * (c.variablePct || 0) / 100)
                 + (c.equityAnnualized || 0)
                 + this.perksProxyCost;
        },
        vcRatio() {
            if (this.totalEmploymentCost === 0) return 0;
            return this.adjustedValue / this.totalEmploymentCost;
        },
        alignmentBand() {
            if (this.totalEmploymentCost === 0) return 'Undefined';
            if (this.vcRatio >= 3) return 'Under-rewarded';
            if (this.vcRatio >= 1) return 'Aligned';
            return 'Over-costed';
        },
        compLogicRecommendation() {
            const totalScore = this.clarityScore + this.attributionScore;
            const directPct = this.form.kvdr.attribution?.directContributionPct || 0;
            
            if (totalScore < 12) return 'Fixed';
            if (totalScore < 15) return 'Fixed + Variable';
            if (directPct < 40) return 'Pooled (Shared Attribution)';
            if (totalScore >= 21 && this.vcRatio >= 3) return 'Value-linked';
            if (directPct >= 70) return 'Project-linked';
            return 'Fixed + Variable';
        },
        agentFitScore() {
            const ag = this.form.kvdr.agentic;
            if(!ag) return 12;
            const painVal = ag.painLevel === 'high' ? 3 : ag.painLevel === 'medium' ? 2 : 1;
            const freqVal = ag.frequency === 'daily' ? 3 : ag.frequency === 'weekly' ? 2 : 1;
            const aiAdvVal = ag.aiAdvantage === 'time' ? 3 : ag.aiAdvantage === 'accuracy' ? 2 : 1;
            return painVal * freqVal * aiAdvVal; // Max 27
        },
        agenticRecommendation() {
            const fit = this.agentFitScore;
            const ag = this.form.kvdr.agentic;
            if(!ag) return { action: 'Do not automate', subtext: 'Incomplete data.' };

            const riskTolerance = ag.riskTolerance || 3;
            const exceptionLoadPct = ag.exceptionLoadPct || 0;
            const kvdrTotalScore = this.clarityScore + this.attributionScore;

            const risky = riskTolerance <= 2 || exceptionLoadPct >= 60;

            if (kvdrTotalScore >= 21 && exceptionLoadPct >= 40) return { action: 'Augment', subtext: 'High value KVDR with many exceptions. Augment, do not replace.' };
            if (risky && fit < 18) return { action: 'Do not automate', subtext: 'High risk or exception load makes automation unviable.' };
            if (fit >= 18 && exceptionLoadPct < 25) return { action: 'Agentize partial', subtext: 'High fit and low exceptions. Prime candidate for agentic scaling.' };
            if (fit >= 12) return { action: 'Hybridize', subtext: 'Solid fit but exceptions or risk warrant human-in-the-loop.' };
            if (fit >= 6) return { action: 'Augment', subtext: 'Use AI to speed up workflows, but keep human ownership.' };
            
            return { action: 'Do not automate', subtext: 'Low AI fit score. Focus on process improvement instead.' };
        },
        estimatedSaving() {
            const fit = this.agentFitScore;
            const ag = this.form.kvdr.agentic;
            if(!ag) return 0;
            const exceptionLoadPct = ag.exceptionLoadPct || 0;
            const automatable = Math.max(0, 1 - (exceptionLoadPct / 100));
            const factor = Math.min(0.6, (fit / 27) * automatable * 0.6); // Capped at 60%
            return Math.round(this.totalEmploymentCost * factor);
        }
    },
    methods: {
        selectDepartment(dept) {
            this.form.department = dept;
            this.showDeptDropdown = false;
        },
        selectLevel(level) {
            this.form.level = level;
            this.showLevelDropdown = false;
        },
        openAdvancedSettings() {
            this.advancedTab = 'attribution'; // Reset to first tab
            this.showAdvancedModal = true;
        },
        selectLevel(level) {
            this.form.level = level;
            this.showLevelDropdown = false;
        },
        updateSliderStyle(event) {
            const slider = event.target;
            const min = slider.min;
            const max = slider.max;
            const val = slider.value;
            const percentage = ((val - min) * 100) / (max - min);
            slider.style.setProperty('--fill-percent', `${percentage}%`);
        },
        
        async open(payload) {
            this.mode = payload.mode || 'CREATE';
            this.onSaveCallback = payload.onSave;
            this.currentView = 'main';
            this.generatedJD = '';
            this.showJDModal = false;
            this.funcSkillSearch = '';
            this.softSkillSearch = '';
            this.showFuncDropdown = false;
            this.showSoftDropdown = false;
            
            let role = { ...(payload.role || {}) };
            await this.fetchLevelsAndConfig();

            const realMongoId = role._id || role.id || null;
            const activeRoleId = role.selfId || realMongoId;

            // --- CRITICAL FIX 1: LOAD FROM ROBUST CACHE FIRST ---
            // Guarantees data survives hard refreshes while database syncs
            const cacheKeyId = realMongoId || activeRoleId;
            if (cacheKeyId) {
                try {
                    const cache1 = localStorage.getItem(`role_extended_data_${cacheKeyId}`);
                    const cache2 = localStorage.getItem(`role_extended_data_${activeRoleId}`);
                    const cacheStr = cache1 || cache2;
                    
                    if (cacheStr) {
                        const cached = JSON.parse(cacheStr);
                        role.mission = cached.mission || role.mission;
                        role.primaryGoal = cached.primaryGoal || role.primaryGoal;
                        role.secondaryGoal = cached.secondaryGoal || role.secondaryGoal;
                        role.roleSummary = cached.roleSummary || role.roleSummary;
                        if (cached.description) role.description = cached.description;
                        if (cached.level) role.level = cached.level;
                    }
                } catch(e) { console.warn("Cache read error", e); }
            }

            // Fetch live API details for existing roles
            if (activeRoleId && this.mode === 'EDIT') { 
                try {
                    const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
                    let apiUrl = process.env.VUE_APP_API_URL;
                    if (!apiUrl || apiUrl === 'undefined') apiUrl = 'https://parser.ai.getboarded.com'; // Fallback added

                    const res = await fetch(`${apiUrl}/api/v2/roles/${activeRoleId}`, { 
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const fetchedRole = await res.json();
                        role.mission = fetchedRole.mission || role.mission;
                        role.primaryGoal = fetchedRole.primaryGoal || role.primaryGoal;
                        role.secondaryGoal = fetchedRole.secondaryGoal || role.secondaryGoal;
                        role.roleSummary = fetchedRole.roleSummary || role.roleSummary;
                        if (fetchedRole.description) role.description = fetchedRole.description;
                        if (fetchedRole.level) role.level = fetchedRole.level;
                        if (fetchedRole.kvdr) role.kvdr = fetchedRole.kvdr;
                    }
                } catch (e) {
                    console.warn("Could not fetch full role details", e);
                }
            }

            let summaryText = role.responsibilities || role.roleSummary || '';
            if (!summaryText && Array.isArray(role.description) && role.description.length > 0) {
                 const lastEntry = [...role.description].reverse().find(d => d && typeof d.msg === 'string');
                 if (lastEntry) summaryText = lastEntry.msg.replace('[ROLE_DESC_SAVE]', '');
            } else if (!summaryText && typeof role.description === 'string') {
                 summaryText = role.description;
            }
            
            // 1. ADD FULL NESTED DEFAULTS
            const nvrDefault = {
                valueDriver: '', valueTags: [], businessExpressions: [], 
                influenceScore: 1, ownershipClarity: 3, otherValueText: '',
                attribution: {
                    painSolved: '', impactFrequency: '', counterfactualLoss: '', measurable: '',
                    attributionStatus: '', directContributionPct: 50, underperformanceSpeed: '',
                    replaceability: [], downstreamDeps: '', flowPosition: '', decisionLevel: '',
                    outcomeOrientation: '', leverageScore: 0 
                },
                quantification: {
                    currency: 'USD', revenueInfluenced: null, costSaved: null, riskReduced: null,
                    cycleTimeValue: null, qualityValue: null, strategicValue: null, notes: '',
                    confidence: 50, lowMidThreshold: 50000, midHighThreshold: 500000
                },
                // NEW LAYER 4: COMPENSATION
                compensation: {
                    baseSalary: null, employmentCostMultiplier: 1.3, variablePct: 0, equityAnnualized: null, 
                    interns: 0, assistant: false, recognitionRewards: false, learningBudget: null, 
                    extraTimeOffDays: 0, flexibilityScore: 3, otherPerks: ''
                },
                agentic: {
                    painLevel: 'medium', frequency: 'weekly', aiAdvantage: 'time', 
                    riskTolerance: 3, exceptionLoadPct: 25, valueUpside: '', agentizeRole: false
                }
            };

            let fetchedKvdr = role.kvdr || {};

            // --- UPDATED: Fetch KVDR Metrics ---
            if (activeRoleId && this.mode === 'EDIT') { 
                try {
                    const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
                    let apiUrl = process.env.VUE_APP_API_URL;
                    if (!apiUrl || apiUrl === 'undefined') apiUrl = 'https://parser.ai.getboarded.com'; // Fallback added

                    // FIX: Using activeRoleId here guarantees selfId (X6qaiRh...) is passed to Python
                    const res = await fetch(`${apiUrl}/api/v2/roles/${activeRoleId}/kvdr-metrics`, { 
                        headers: { 'Authorization': `Bearer ${token}` } 
                    });
                    
                    if (res.ok) {
                        const json = await res.json();
                        fetchedKvdr = json.data?.kvdr || json.kvdr || fetchedKvdr;
                    }
                } catch (e) { 
                    console.warn("Could not fetch KVDR metrics"); 
                }
            }

            // 2. DEEP MERGE TO PREVENT UNDEFINED CRASHES
            let finalKvdr = { 
                ...nvrDefault, 
                ...fetchedKvdr,
                attribution: { ...nvrDefault.attribution, ...(fetchedKvdr.attribution || {}) },
                quantification: { ...nvrDefault.quantification, ...(fetchedKvdr.quantification || {}) },
                compensation: { ...nvrDefault.compensation, ...(fetchedKvdr.compensation || {}) },
                agentic: { ...nvrDefault.agentic, ...(fetchedKvdr.agentic || {}) } // NEW
            };

            let savedLevel = role.level || '';
            let savedDept = role.department || '';
            if (activeRoleId && this.mode === 'EDIT') {
                try {
                    const apiUrl = process.env.VUE_APP_API_URL;
                    // Fetch from the API which now returns both level & department!
                    const lvlRes = await fetch(`${apiUrl}/api/role/${activeRoleId}/level`);
                    if (lvlRes.ok) {
                        const lvlJson = await lvlRes.json();
                        if (lvlJson.status === 'success') {
                            savedLevel = lvlJson.level || savedLevel;
                            savedDept = lvlJson.department || savedDept; // ✨ NEW: Extract department
                        }
                    }
                } catch(e) { console.warn("Could not fetch role level and department", e); }
            }
            
            let mappedProfiles = [];
            if (this.mode === 'EDIT') {
                const candidateList = role.candidateList || [];
                mappedProfiles = (role.profiles || []).map(p => {
                    const uid = p.userId || p.id;
                    const cand = candidateList.find(c => c.id === uid || c.user_id === uid) || {};
                    return { ...cand, ...p, id: uid }; 
                }).filter(p => {
                    // 🚀 THE FIX: Hard filter out deactivated users before the modal renders them
                    const isInactive = p.isActive === false || p.isDeleted === true || 
                                       (p.status && ['inactive', 'deleted', 'removed', 'deactivated'].includes(String(p.status).toLowerCase())) ||
                                       (p.userData && (p.userData.isActive === false || p.userData.isDeleted === true || 
                                       (p.userData.status && ['inactive', 'deleted', 'removed', 'deactivated'].includes(String(p.userData.status).toLowerCase()))));
                    return !isInactive;
                });
            }
            
            this.form = {
                _id: realMongoId,
                selfId: activeRoleId || null,
                title: role.title || '',
                level: savedLevel, department: savedDept,
                mission: role.mission || '',
                responsibilities: summaryText, 
                primaryGoal: role.primaryGoal || '',
                secondaryGoal: role.secondaryGoal || '',
                skills: role.skills ? [...role.skills] : [],
                softSkills: role.softSkills ? [...role.softSkills] : [],
                profiles: mappedProfiles,
                kvdr: finalKvdr
            };
            
            if (this.mode === 'CREATE') {
                this.form.kvdr.influenceScore = 1;
            }
            
            this.isOpen = true;
        },

        async changeStep(step) {
            // Change the tab immediately for the user
            this.advancedTab = step;
            // Trigger a silent background save to API & LocalStorage
            await this.saveDetectionMetrics(true);
        },

        async saveDetectionMetrics(silent = false) {
            if (this.mode === 'CREATE' || (!this.form.selfId && !this.form._id)) {
                if (!silent) this.currentView = 'main';
                return;
            }

            if (!silent) this.isSavingKvdr = true;
            try {
                const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
                let apiUrl = process.env.VUE_APP_API_URL;
                if (!apiUrl || apiUrl === 'undefined') apiUrl = 'https://parser.ai.getboarded.com';
                
                const payload = {
                    kvdr: this.form.kvdr,
                    kvdrTag: this.kvdrTag,
                    clarityScore: this.clarityScore
                };

                const targetId = this.form.selfId || this.form._id;

                const res = await fetch(`${apiUrl}/api/v2/roles/${targetId}`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (res.ok) {
                    // Update LocalStorage instantly so data survives hard page refreshes
                    localStorage.setItem(`role_extended_data_${targetId}`, JSON.stringify({
                        mission: this.form.mission,
                        primaryGoal: this.form.primaryGoal,
                        secondaryGoal: this.form.secondaryGoal,
                        level: this.form.level,
                        department: this.form.department,
                        roleSummary: this.form.responsibilities,
                        kvdr: this.form.kvdr
                    }));

                    // Only close the modal and show popups if it's an EXPLICIT save button click
                    if (!silent) {
                        if (this.$vs) this.$vs.notify({ title: 'Success', text: 'Classification updated.', color: 'success' });
                        const updatedData = { ...this.form, kvdrTag: this.kvdrTag, clarityScore: this.clarityScore };
                        window.dispatchEvent(new CustomEvent('kvdr-sandbox-refresh-node', { detail: { roleId: this.form.selfId, data: updatedData } }));
                        this.currentView = 'main';
                    }
                } else {
                    throw new Error("Failed to save metrics");
                }
            } catch (error) {
                console.error("Error saving detection metrics:", error);
                if (!silent && this.$vs) {
                    this.$vs.notify({ title: 'Error', text: 'Could not save classification.', color: 'danger' });
                }
            } finally {
                if (!silent) this.isSavingKvdr = false;
            }
        },

        async saveAnalysis() {
            // Visible save triggered by the final "Save analysis" button
            await this.saveDetectionMetrics(false);
            this.showAdvancedModal = false;
        },

        close() { this.isOpen = false; },

        async saveRole() {
            const descriptionArray = this.form.responsibilities ? [{
                msg: this.form.responsibilities + '[ROLE_DESC_SAVE]',
                time: new Date().toISOString()
            }] : [];

            // Temporary ID for initial creation UI (needed for chart rendering)
            if (this.mode === 'CREATE' && !this.form.selfId) {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
                this.form.selfId = [...Array(24)].map(() => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
            }

            const updatedData = {
                ...this.form,
                roleSummary: this.form.responsibilities, department: this.form.department,
                description: descriptionArray, 
                kvdrTag: this.kvdrTag,
                clarityScore: this.clarityScore
            };

            if (this.mode === 'CREATE') {
                updatedData.profiles = [];
                updatedData.kvdrRoleProfiles = [];
                updatedData.candidateList = [];
                updatedData.users = [];
            }

            // 1. SAVE TO FEATHERS JS (Primary DB)
            let savedRes = null;
            if (this.onSaveCallback) {
                try {
                    // AWAIT the Feathers save process
                    savedRes = await this.onSaveCallback(updatedData);
                } catch(e) { console.error("Error saving role to Feathers:", e); }
            }

            // 2. EXTRACT TRUE IDs (CRITICAL FIX: Handle Feathers Array Responses!)
            let realMongoId = this.form._id;
            let realSelfId = this.form.selfId;

            if (savedRes) {
                if (savedRes.data && Array.isArray(savedRes.data) && savedRes.data.length > 0) {
                    // It returned inside a paginated { data: [] } object
                    realMongoId = savedRes.data[0]._id || realMongoId;
                    realSelfId = savedRes.data[0].selfId || realSelfId;
                } else if (Array.isArray(savedRes) && savedRes.length > 0) {
                    // It returned as a direct array []
                    realMongoId = savedRes[0]._id || realMongoId;
                    realSelfId = savedRes[0].selfId || realSelfId;
                } else {
                    // It returned as a flat object {}
                    realMongoId = savedRes._id || realMongoId;
                    realSelfId = savedRes.selfId || realSelfId;
                }
            }

            // We must sync to Python using the real ID
            const syncId = realSelfId || realMongoId;

            if (syncId) {
                this.form.selfId = realSelfId; // Update form state
                this.form._id = realMongoId;

                // Save to local cache so data stays immediately visible on refresh
                localStorage.setItem(`role_extended_data_${syncId}`, JSON.stringify({
                    mission: this.form.mission,
                    primaryGoal: this.form.primaryGoal,
                    secondaryGoal: this.form.secondaryGoal,
                    level: this.form.level,
                    department: this.form.department,
                    roleSummary: this.form.responsibilities,
                    description: descriptionArray,
                    kvdr: this.form.kvdr
                }));

                const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
                let apiUrl = process.env.VUE_APP_API_URL;
                if (!apiUrl || apiUrl === 'undefined') apiUrl = 'https://parser.ai.getboarded.com';

                const syncToPython = async () => {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // ✨ CRITICAL UPDATE: Ensure we push BOTH level and department to the metadata API!
                    if (this.form.level || this.form.department) {
                        try {
                            const metaPayload = {
                                role_id: syncId,
                                level: this.form.level || "",
                                department: this.form.department || ""
                            };
                            
                            // Send to the Level API (Legacy fallback)
                            await fetch(`${apiUrl}/api/role/level`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify(metaPayload)
                            });

                            // ALSO send to the Metadata API to guarantee it writes to SQLite!
                            await fetch(`${apiUrl}/api/v2/roles/${syncId}/metadata`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                body: JSON.stringify(metaPayload)
                            });
                        } catch (e) { console.warn("Failed to sync level/dept", e); }
                    }

                    // Sync Extended Data (Description, Mission, etc)
                    try {
                        const patchRes = await fetch(`${apiUrl}/api/v2/roles/${syncId}`, {
                            method: 'PATCH',
                            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                            body: JSON.stringify(updatedData)
                        });
                        
                        if (!patchRes.ok) {
                            console.error(`Python API returned ${patchRes.status} for ID ${syncId}`);
                            // Try fallback to Mongo ID if selfId failed
                            if (patchRes.status === 404 && realMongoId && syncId !== realMongoId) {
                                await fetch(`${apiUrl}/api/v2/roles/${realMongoId}`, {
                                    method: 'PATCH',
                                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                    body: JSON.stringify(updatedData)
                                });
                            }
                        }
                    } catch (e) {
                        console.error("Failed to sync extended data to Python API", e);
                    }
                };
                
                syncToPython(); 
            }

            this.close();
        },

        resizeTextarea(e) {
            e.target.style.height = '42px';
            if (e.target.scrollHeight > 42) {
                e.target.style.height = e.target.scrollHeight + 'px';
            }
        },

        formatText(command, value = null) { document.execCommand(command, false, value); },
        updateRichText(field, event) { this.form[field] = event.target.innerHTML; },

        addSkill(type, skill) {
            if (type === 'functional') {
                if (!this.form.skills.includes(skill)) this.form.skills.push(skill);
            } else {
                if (!this.form.softSkills.includes(skill)) this.form.softSkills.push(skill);
            }
        },

        async fetchLevelsAndConfig() {
             try {
                const orgId = this.$store.state.organization.orgData._id;
                const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
                const res = await fetch(`https://api-core-gateway.getboarded.com/organization/organization-config/${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const json = await res.json();
                    if (json.data) {
                        if (json.data.level && json.data.level.values) {
                            this.orgLevels = json.data.level.values; 
                        }
                        // ✨ NEW: Fetch departments
                        if (json.data.department && json.data.department.values) {
                            this.orgDepartments = json.data.department.values;
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch org config:", err);
            }
        },

        addSkillsFromInput(type) {
            const searchModel = type === 'functional' ? 'funcSkillSearch' : 'softSkillSearch';
            const skillsArray = type === 'functional' ? this.form.skills : this.form.softSkills;

            const skillsToAdd = this[searchModel].split(',').map(s => s.trim()).filter(s => s.length > 0);
            skillsToAdd.forEach(skill => {
                if (!skillsArray.find(s => s.toLowerCase() === skill.toLowerCase())) skillsArray.push(skill);
            });
            this[searchModel] = '';
        },
        
        removeSkill(type, index) {
            if (type === 'functional') this.form.skills.splice(index, 1);
            else this.form.softSkills.splice(index, 1);
        },
        
        async generateAIContent() {
            if (!this.form.title) {
                if (this.$vs) this.$vs.notify({ title: 'Title Required', text: 'Please enter a Role Title first.', color: 'warning' });
                return;
            }
            this.isGeneratingJD = true;
            try {
                const token = localStorage.getItem('accessToken') || localStorage.getItem('feathers-jwt');
                let apiUrl = process.env.VUE_APP_API_URL; 
                if (!apiUrl || apiUrl === 'undefined') apiUrl = 'https://parser.ai.getboarded.com';
                
                const payload = {
                    role_title: this.form.title,
                    level: this.form.level || "",
                    technical_skills: this.form.skills, 
                    soft_skills: this.form.softSkills,
                    existing_description: this.form.responsibilities
                };
                
                if (this.mode === 'EDIT' && this.form._id) {
                    payload.role_id = this.form._id;
                } else {
                    payload.role_id = "new_role"; 
                }

                const response = await fetch(`${apiUrl}/api/generate-role-description`, {
                    method: 'POST', 
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
                const data = await response.json();
                
                if (data.description || data.role_description) this.form.responsibilities = data.description || data.role_description;
                
                if (data.recommended_technical_skills) {
                    const newSkills = data.recommended_technical_skills.filter(skill => !this.form.skills.find(s => s.toLowerCase() === skill.toLowerCase()));
                    this.funcSkillSearch = newSkills.join(', ');
                }

                if (data.recommended_soft_skills) {
                    const newSkills = data.recommended_soft_skills.filter(skill => !this.form.softSkills.find(s => s.toLowerCase() === skill.toLowerCase()));
                    this.softSkillSearch = newSkills.join(', ');
                }
                if (this.$vs) this.$vs.notify({ title: 'Success', text: 'AI successfully generated role details!', color: 'success' });
            } catch (error) {
                console.error("AI Generation Error: ", error);
                if (this.$vs) this.$vs.notify({ title: 'Error', text: 'Failed to generate details. Try again.', color: 'danger' });
            } finally {
                this.isGeneratingJD = false;
            }
        },

        toggleArrayItem(arr, item) {
            const index = arr.indexOf(item);
            if (index > -1) arr.splice(index, 1);
            else arr.push(item);
        },
        triggerTalentCard(userId) { window.dispatchEvent(new CustomEvent('show-talent-card', { detail: { userId } })); },
        
        getAvatarUrl(user) {
            const url = user.imageUrl || user.image_url || user.profilePictureUrl || user.avatar;
            if (url && url.startsWith('http')) return url;
            if (url) return `https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${url}`;
            return `https://hostedboringavatars.vercel.app/api/beam?name=${encodeURIComponent(user.name || user.firstName || 'User')}`;
        },

        openJDPreview() {
            const t = (key) => this.$t(`orgDashboard.userDashboard.evaluation.${key}`);
            this.generatedJD = `
                <h3 style="font-size: 22px; color: #0f172a; margin-bottom: 8px;">
                    ${t('jobdescription')}: ${this.form.title || t('untitledrole')}
                </h3>
                <p style="margin: 4px 0;"><strong>Level:</strong> ${this.form.level || t('notspecified')}</p>
                <p style="margin: 4px 0;"><strong>${t('mission')}:</strong> ${this.form.mission || t('todriveorgsuccess')}</p>
                <p style="margin: 4px 0;"><strong>Classification:</strong> ${this.kvdrTag} ${t('role')}</p>
                
                <h4 style="margin-top: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">${t('roledescription')}</h4>
                <div style="line-height: 1.6;">${this.form.responsibilities || `<p>${t('nodescription')}</p>`}</div>
                
                <h4 style="margin-top: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">${t('functionalSkills')}</h4>
                <ul style="padding-left: 20px;">
                    ${this.form.skills.length ? this.form.skills.map(s => `<li>${s}</li>`).join('') : `<li>${t('nonespecified')}</li>`}
                </ul>
                
                <h4 style="margin-top: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">${t('softSkills')}</h4>
                <ul style="padding-left: 20px;">
                    ${this.form.softSkills.length ? this.form.softSkills.map(s => `<li>${s}</li>`).join('') : `<li>${t('nonespecified')}</li>`}
                </ul>
                
                <h4 style="margin-top: 20px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">${t('goals')}</h4>
                <p style="margin: 4px 0;"><strong>${t('primarygoal')}:</strong> ${this.form.primaryGoal || t('notspecified')}</p>
                <p style="margin: 4px 0;"><strong>${t('secondarygoal')}:</strong> ${this.form.secondaryGoal || t('notspecified')}</p>
            `;
            this.showJDModal = true;
        },

        downloadJD(format) {
            let content = this.generatedJD;
            let filename = `${this.form.title.replace(/\s+/g, '_')}_JD`;
            let type = '';

            if (format === 'txt') {
                content = content.replace(/<[^>]+>/g, '\n'); 
                filename += '.txt';
                type = 'text/plain';
            } else if (format === 'doc') {
                content = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>${content}</body></html>`;
                filename += '.doc';
                type = 'application/msword';
            } else if (format === 'pdf') {
                 const win = window.open('', '', 'height=700,width=700');
                 win.document.write(`<html><head><title>${filename}</title><style>body{font-family:sans-serif; padding:30px; color: #333; line-height: 1.6;}</style></head><body>${content}</body></html>`);
                 win.document.close();
                 setTimeout(() => win.print(), 500); 
                 return;
            }

            const blob = new Blob([content], { type });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        },

        async fetchLevels() {
             try {
                const orgId = this.$store.state.organization.orgData._id;
                const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
                const res = await fetch(`https://api-core-gateway.getboarded.com/organization/organization-config/${orgId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const json = await res.json();
                    if (json.data && json.data.level && json.data.level.values) {
                        this.orgLevels = json.data.level.values; 
                    }
                }
            } catch (err) {
                console.error("Failed to fetch org levels:", err);
            }
        },
        formatNumber(num) {
            if (num === null || num === undefined) return '0';
            return Number(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
        },

        // LAYER 2: PILLAR FORMULAS (1:1 with KVDR Guide)
        calcScoreBlock(blockName) {
            const a = this.form.kvdr.attribution;
            if (!a) return 0;
            let score = 0;
            
            if (blockName === 'criticality') {
                score += (a.painSolved === 'high' ? 2 : a.painSolved === 'medium' ? 1 : 0);
                score += (a.impactFrequency === 'daily' ? 2 : a.impactFrequency === 'weekly' ? 1.5 : a.impactFrequency === 'monthly' ? 1 : 0);
                score += ((a.counterfactualLoss || '').length >= 20 ? 1 : 0);
            } 
            else if (blockName === 'value') {
                score += (a.measurable === 'tangible' ? 2.5 : a.measurable === 'intangible' ? 1 : 0);
                score += (a.attributionStatus === 'yes' ? 2.5 : a.attributionStatus === 'partially' ? 1.5 : 0);
            } 
            else if (blockName === 'risk') {
                score += (a.underperformanceSpeed === 'immediately' ? 2 : a.underperformanceSpeed === 'short-term' ? 1.5 : a.underperformanceSpeed === 'medium-term' ? 0.5 : 0);
                score += ((a.replaceability || []).includes('Not easily replaceable') ? 1 : 0);
                score += ((a.leverageScore || 0) / 5) * 2;
            } 
            else if (blockName === 'influence') {
                score += (a.downstreamDeps === '5+' ? 1.5 : a.downstreamDeps === '3-5' ? 1 : a.downstreamDeps === '1-2' ? 0.5 : 0);
                score += (a.flowPosition === 'unblocks' ? 1 : 0);
                score += (a.decisionLevel === 'strategic' ? 1.5 : a.decisionLevel === 'high' ? 1 : a.decisionLevel === 'some' ? 0.5 : 0);
                score += (a.outcomeOrientation === 'owner' ? 1 : a.outcomeOrientation === 'influencer' ? 0.75 : a.outcomeOrientation === 'optimizer' ? 0.25 : 0);
            }
            return Math.min(5, score); // Cap each pillar at 5
        }
    },
    watch: {
        currentView(newVal) {
            // When the view switches TO the detection layer...
            if (newVal === 'detection') {
                // Wait for the DOM to finish rendering the sliders
                this.$nextTick(() => {
                    // Find ONLY the sliders inside the detection modal
                    const sliders = this.$el.querySelectorAll('.detection-modal .slider');
                    if (sliders.length > 0) {
                        sliders.forEach(slider => {
                            // This logic is the same as before, but now it runs at the right time
                            const min = slider.min;
                            const max = slider.max;
                            const val = slider.value; // v-model ensures this is the correct current value
                            const percentage = ((val - min) * 100) / (max - min);
                            slider.style.setProperty('--fill-percent', `${percentage}%`);
                        });
                    }
                });
            }
        }
    }
}

</script>

<style scoped>
.role-editor-overlay { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background: rgba(15, 23, 42, 0.5); display: flex; align-items: center; justify-content: center; z-index: 20000; backdrop-filter: blur(2px); }
.role-editor-modal { background: #fff; width: 900px; max-height: 95vh; border-radius: 12px; display: flex; flex-direction: column; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); animation: modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes modalIn { from { opacity: 0; transform: translateY(20px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }

.modal-header { padding: 20px 24px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; background: #fff; border-radius: 12px 12px 0 0; }
.modal-title { font-size: 18px; font-weight: 700; color: #0f172a; margin: 0; }
.subtitle { font-weight: 400; color: #64748b; font-size: 14px; }
.header-right { display: flex; align-items: center; gap: 12px; }
.close-btn { background: none; border: none; font-size: 24px; cursor: pointer; color: #94a3b8; }
.close-btn:hover { color: #0f172a; }

.modal-body { padding: 20px 24px; overflow-y: auto; flex: 1; display: flex; flex-direction: column; }
.main-layout-container { display: flex; gap: 28px; margin-top: 10px; }
.main-content-column { flex: 2; display: flex; flex-direction: column; gap: 18px; min-width: 0; }
.skills-column { flex: 1; display: flex; flex-direction: column; gap: 24px; min-width: 0; border-left: 1px solid #f1f5f9; padding-left: 20px; }

.modal-footer { padding: 16px 24px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; background: #fff; border-radius: 0 0 12px 12px; }

.form-group { display: flex; flex-direction: column; gap: 6px; }
.form-group label { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0; }
.form-control { width: 100%; padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 14px; font-family: inherit; transition: border-color 0.2s; box-sizing: border-box; outline: none; }
.form-control:focus { border-color: #F59E0B; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1); }
.form-group-box { padding: 16px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; display: flex; flex-direction: column; gap: 12px; }
.box-title { display: flex; align-items: center; gap: 8px; font-weight: 700; color: #0f172a; font-size: 12px; }

.designation-input { width: 100%; padding: 8px 0; border: none; border-bottom: 2px solid #e2e8f0; border-radius: 0; font-size: 24px; font-weight: 700; color: #1e293b; background: transparent; outline: none; transition: border-color 0.2s; }
.designation-input::placeholder { color: #cbd5e1; font-weight: 600; }
.designation-input:focus { border-bottom-color: #F59E0B; }

.level-mission-row { display: flex; gap: 16px; align-items: flex-start; }
.select-fixed-height { height: 42px; }
.auto-expand-textarea { min-height: 42px; height: 42px; resize: none; overflow: hidden; line-height: 1.4; } 

.search-wrapper { margin-top: 4px; margin-bottom: 8px; position: relative; }
.tags-container { display: flex; flex-wrap: wrap; gap: 6px; min-height: 28px; }
.skill-tag { background: #fef3c7; color: #d97706; padding: 4px 10px; border-radius: 16px; font-size: 12px; display: flex; align-items: center; gap: 6px; border: 1px solid #fde68a; }
.remove-skill { cursor: pointer; font-weight: bold; }

/* Modified Custom Dark Floating Dropdown List */
.minimal-input-wrapper {
    position: relative;
    margin-bottom: 8px;
    margin-top: 4px;
}
.minimal-input {
    width: 100%;
    padding: 8px 24px 8px 0;
    border: none;
    border-bottom: 2px solid #e2e8f0;
    border-radius: 0;
    font-size: 15px;
    color: #475569;
    background: transparent;
    outline: none;
    transition: border-color 0.2s;
}
.minimal-input::placeholder {
    color: #64748b;
    font-weight: 400;
}
.minimal-input:focus {
    border-bottom-color: #F59E0B;
    box-shadow: none;
}
.dropdown-caret {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #0f172a;
    pointer-events: none;
}

.side-dropdown-list { 
    position: absolute; 
    top: calc(100% + 4px); /* Opens directly below the input */
    left: 0; 
    width: 100%; /* Matches input width perfectly */
    z-index: 10000; 
    list-style: none; 
    margin: 0; 
    padding: 8px 0; 
    max-height: 250px; 
    overflow-y: auto; 
    border-radius: 8px; 
    background: #0f172a; 
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.6); 
}
.side-dropdown-list li { 
    padding: 10px 16px; 
    font-size: 13px; 
    color: #e2e8f0; 
    border-bottom: none; 
    cursor: pointer; 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    transition: background 0.1s, color 0.1s; 
}
.side-dropdown-list li:hover { 
    background: #1e293b; 
    color: #fff; 
}
.side-dropdown-list::-webkit-scrollbar { width: 6px; }
.side-dropdown-list::-webkit-scrollbar-track { background: transparent; }
.side-dropdown-list::-webkit-scrollbar-thumb { background: #475569; border-radius: 4px; }
.check-icon { color: #34d399; font-weight: bold; font-size: 14px; }
.no-results { color: #64748b !important; font-style: italic; cursor: default !important; }
.no-results:hover { background: #0f172a !important; color: #64748b !important; }

/* JD Preview Modal Layout Fixes */
.jd-preview-overlay { z-index: 21000; }
.jd-preview-modal { 
    background: #fff; 
    width: 750px; 
    max-height: 90vh; 
    border-radius: 12px; 
    display: flex; 
    flex-direction: column; 
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4); 
    animation: modalIn 0.2s ease-out; 
    overflow: hidden; /* Restrict bleeding over border-radius */
}
.jd-toolbar { padding: 12px 24px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; gap: 12px; align-items: center; }
.toolbar-label { font-size: 13px; font-weight: 600; color: #64748b; margin-right: 4px; }
.dl-btn { display: flex; align-items: center; gap: 6px; padding: 6px 14px; background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 13px; font-weight: 600; color: #475569; cursor: pointer; transition: all 0.2s; }
.dl-btn svg { width: 14px; height: 14px; color: #F59E0B; }
.dl-btn:hover { background: #fefce8; border-color: #fde68a; color: #b45309; }
.jd-body { 
    padding: 30px; 
    padding-bottom: 60px !important; /* Extra bottom padding prevents Goals from clipping */
    background: #fff; 
    flex: 1; 
    overflow-y: auto; 
}
.readonly-jd { 
    border: none !important; /* Removes the inner gray box border */
    padding: 0 !important; 
    margin-bottom: 20px; /* Safe space at the bottom */
    font-family: inherit; 
    color: #334155; 
}
.label-row-with-ai { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2px; }
.ai-icon-btn { background: transparent; border: none; color: #64748b; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 4px; border-radius: 4px; transition: color 0.2s, background-color 0.2s; }
.ai-icon-btn:hover:not(:disabled) { background: #fefce8; color: #F59E0B; }
.loading-spinner { width: 14px; height: 14px; border: 2px solid #F59E0B; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; }
.rich-text-toolbar { display: flex; gap: 4px; padding: 4px 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-bottom: none; border-radius: 6px 6px 0 0; }
.rich-text-toolbar button, .rich-text-toolbar select { background: none; border: 1px solid transparent; border-radius: 4px; cursor: pointer; padding: 4px 8px; font-size: 14px; color: #475569; }
.rich-text-toolbar button:hover { background: #e2e8f0; }
.rich-text-editor { min-height: 120px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 0 0 6px 6px; font-size: 14px; outline: none; }
.rich-text-editor:focus { border-color: #F59E0B; }
.rich-text-editor >>> ul { list-style-type: disc !important; padding-left: 24px !important; margin-top: 8px !important; margin-bottom: 8px !important; }
.rich-text-editor >>> ol { list-style-type: decimal !important; padding-left: 24px !important; margin-top: 8px !important; margin-bottom: 8px !important; }
.rich-text-editor >>> li { display: list-item !important; list-style-position: outside !important; margin-bottom: 4px !important; }

.kvdr-badge { padding: 4px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; cursor: pointer; transition: 0.2s; position: relative; }
.kvdr-badge.nvr { background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; }
.kvdr-badge.svr { background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; }
.kvdr-badge.kvdr {
    background: #E6F7F0;
    color: #067647;
    border: 1px solid #A3E6C8;
}
.kvdr-badge:hover { filter: brightness(0.95); }
.badge-tooltip {
  position: absolute;
  top: 110%;
  left: 0;              /* anchor to left */

  right: auto;          /* ❌ remove stretching */
  width: max-content;   /* shrink to content */
  max-width: 220px;     /* optional cap */

  background: #1e293b;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 11px;

  text-align: left;     /* 👈 actual left-aligned text */
  font-weight: 400;

  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  z-index: 50;
}
.kvdr-badge:hover .badge-tooltip { visibility: visible; opacity: 1; }

.assigned-candidates-section { background: #f8fafc; border: 1px solid #e2e8f0; padding: 12px 16px; border-radius: 8px; margin-bottom: 10px; }
.assigned-candidates-section label { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 10px; text-transform: uppercase; }
.candidate-avatars { display: flex; gap: 8px; flex-wrap: wrap; }
.avatar-wrapper { position: relative; cursor: pointer; transition: transform 0.2s; }
.avatar-wrapper:hover { transform: translateY(-2px); z-index: 10; }
.candidate-avatar { width: 36px; height: 36px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); object-fit: cover; }
.avatar-wrapper .tooltip { position: absolute; bottom: -25px; left: 50%; transform: translateX(-50%); background: #1e293b; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; opacity: 0; pointer-events: none; white-space: nowrap; transition: 0.2s; }
.avatar-wrapper:hover .tooltip { opacity: 1; }

.classification-box { border-radius: 8px; padding: 16px; display: flex; justify-content: space-between; align-items: center; }
.classification-box.nvr { background: #fff; border: 1px solid #e2e8f0; }
.classification-box.nvr .class-label { color: #64748b; }
.classification-box.nvr .class-val, .classification-box.nvr .class-score { color: #1e293b; }
.classification-box.svr { background: #e0f2fe; border: 1px solid #bae6fd; }
.classification-box.svr .class-label { color: #0284c7; }
.classification-box.svr .class-val, .classification-box.svr .class-score { color: #0369a1; }
.classification-box.kvdr { background: #fef3c7; border: 1px solid #fde68a; }
.classification-box.kvdr .class-label { color: #b45309; }
.classification-box.kvdr .class-val, .classification-box.kvdr .class-score { color: #92400e; }
.class-label { font-size: 10px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
.class-val { font-size: 24px; font-weight: 800; }
.class-desc { font-size: 12px; color: #0284c7; margin-top: 4px; }
.class-score { font-size: 20px; font-weight: 700; }
.class-score span { font-size: 28px; }
.btn-group { display: flex; gap: 8px; }
.btn-group button { flex: 1; padding: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; cursor: pointer; font-weight: 600; color: #475569; }
.btn-group button.active { background: #fef3c7; border-color: #fde68a; color: #d97706; }
.pill-group { display: flex; flex-wrap: wrap; gap: 8px; }
.pill { padding: 6px 14px; border-radius: 20px; border: 1px solid #cbd5e1; background: #fff; font-size: 13px; color: #475569; cursor: pointer; }
.pill.active { background: #fef3c7; border-color: #fde68a; color: #d97706; font-weight: 600; }
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.slider { width: 100%; appearance: none; background: #e2e8f0; height: 6px; border-radius: 4px; outline: none; margin-top: 8px; }
.slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #3b82f6; cursor: pointer; }
.yellow-slider::-webkit-slider-thumb { background: #F59E0B; }
.slider-labels { display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; margin-top: 6px; }
.score-badge { float: right; background: #e2e8f0; padding: 2px 8px; border-radius: 10px; color: #0f172a; }
.clarity-score-box { text-align: center; background: #fff; padding: 12px; border-radius: 6px; border: 1px dashed #cbd5e1; color: #475569; font-size: 14px; }

.btn-secondary { padding: 10px 20px; background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; font-weight: 600; color: #475569; cursor: pointer; transition: 0.2s; }
.btn-secondary:hover { background: #f8fafc; }
.btn-primary { padding: 10px 20px; background: #FBBF24; border: none; border-radius: 6px; font-weight: 600; color: #fff; cursor: pointer; transition: opacity 0.2s; }
.btn-primary:hover { opacity: 0.9; }
.invisible-scroll-y::-webkit-scrollbar { width: 6px; }
.invisible-scroll-y::-webkit-scrollbar-track { background: transparent; }
.invisible-scroll-y::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

.title-level-wrapper { 
    display: flex; 
    align-items: baseline; 
    border-bottom: 2px solid #e2e8f0; 
    padding-bottom: 4px; 
    margin-bottom: 12px; 
    transition: border-color 0.2s; 
}
.title-level-wrapper:focus-within { 
    border-bottom-color: #F59E0B; 
}

/* Stripped down title input */
.designation-input.minimal-title { 
    flex: 1; 
    border: none; 
    padding: 0; 
    margin: 0; 
}
.designation-input.minimal-title:focus { 
    border-bottom-color: transparent; /* Handled by wrapper now */
}

/* Minimal Level Selector attached to the right */
.level-selector { 
    position: relative; 
    display: inline-flex; 
    align-items: center; 
}
.level-select-minimal { 
    appearance: none; 
    background: transparent; 
    border: none; 
    padding: 4px 20px 4px 8px; 
    font-size: 13px; 
    font-weight: 600; 
    color: #64748b; 
    cursor: pointer; 
    outline: none; 
    text-align: right;
}
.level-select-minimal:focus, .level-select-minimal:hover { 
    color: #F59E0B; 
}
.level-selector .dropdown-caret { 
    position: absolute; 
    right: 4px; 
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px; 
    color: #94a3b8; 
    pointer-events: none; 
}

/* Minimal Mission/Description Input */
.mission-group { 
    margin-bottom: 24px; 
}

.minimal-mission-input { 
    width: 100%; 
    padding: 8px 0; 
    border: none; 
    border-bottom: 2px solid #e2e8f0; /* Adds the line beneath */
    border-radius: 0;
    font-size: 14px; 
    color: #1e293b; 
    background: transparent; 
    outline: none; 
    line-height: 1.5;
    font-family: inherit;
    transition: border-color 0.2s;
}

.minimal-mission-input::placeholder { 
    color: #cbd5e1; 
    font-weight: 500; 
}

/* Adds the yellow highlight when you click into the input */
.minimal-mission-input:focus {
    border-bottom-color: #F59E0B;
}

.minimal-goals-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 8px;
}

/* Minimal Underlined Input Style */
.minimal-underline-input {
    width: 100%;
    padding: 8px 0;
    border: none;
    border-bottom: 2px solid #e2e8f0;
    border-radius: 0;
    font-size: 14px;
    color: #1e293b;
    background: transparent;
    outline: none;
    font-family: inherit;
    transition: border-color 0.2s;
}

.minimal-underline-input::placeholder {
    color: #cbd5e1;
    font-weight: 500;
}

.minimal-underline-input:focus {
    border-bottom-color: #F59E0B;
}
.detection-modal .modal-body {
    gap: 22px;
    background: #f8fafc; /* Light grey background for the whole panel */
    display: flex;
    flex-direction: column;
    gap: 28px; /* Consistent spacing between sections */
    padding: 24px;
}

/* General form group and label styling */
.detection-modal .form-group {
    gap: 12px;
}
.detection-modal .form-group label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    text-transform: none; /* Change from uppercase */
    letter-spacing: 0;
}
.detection-modal .label-hint {
    font-weight: 400;
    color: #94a3b8;
}

/* 1. CLASSIFICATION BOX REDESIGN */
.classification-box {
    border-radius: 8px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border: 1px solid transparent;
}
.classification-box.kvdr {
    background: #E6F7F0; /* Light Green */
    border-color: #A3E6C8;
}
.classification-box.kvdr .class-val, .classification-box.kvdr .class-score { color: #067647; /* Dark Green */}
.classification-box.kvdr .class-label, .classification-box.kvdr .class-desc { color: #059669; }

.classification-box.svr {
    background: #E0F2FE; /* Light Blue */
    border-color: #bae6fd;
}
.classification-box.svr .class-val, .classification-box.svr .class-score { color: #0369a1; }
.classification-box.svr .class-label, .classification-box.svr .class-desc { color: #0284c7; }

.classification-box.nvr {
    border-color: #cbd5e1;
    background: #f1f5f9;
}
.classification-box.nvr .class-val, .classification-box.nvr .class-score { color: #64748b;}
.classification-box.nvr .class-label, .classification-box.nvr .class-desc { color: #64748b; }

.class-label { font-size: 11px; font-weight: 700; margin-bottom: 2px; text-transform: uppercase; }
.class-val { font-size: 22px; font-weight: 700; }
.class-desc { font-size: 13px; margin-top: 4px; }
.class-right { text-align: right; }
.class-score { font-size: 18px; font-weight: 600; }
.class-score span { font-size: 24px; font-weight: 700; }

/* 2. VALUE DRIVER BUTTONS REDESIGN */
.btn-group { display: flex; gap: 12px; }
.btn-group button {
    flex: 1;
    padding: 12px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    color: #475569;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
}
.btn-group button svg { color: #94a3b8; transition: color 0.2s; }
.btn-group button:hover { border-color: #cbd5e1; background: #f8fafc; }
.btn-group button.active {
    background: #FFFBEB; /* Light Orange */
    border-color: #F59E0B; /* Orange */
    color: #B45309; /* Dark Orange */
}
.btn-group button.active svg { color: #F59E0B; }

/* 3. VALUE PILLS REDESIGN */
.pill-group { display: flex; flex-wrap: wrap; gap: 8px; }
.pill {
    padding: 6px 16px;
    border-radius: 20px;
    border: 1px solid #cbd5e1;
    background: #fff;
    font-size: 14px;
    color: #475569;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
}
.pill:hover { border-color: #94a3b8; }
.pill.active {
    background: #F59E0B; /* Orange */
    border-color: #F59E0B;
    color: #fff;
    font-weight: 600;
}
.other-value-input { margin-top: 8px; }


/* 4. BUSINESS TERMS CHECKBOXES REDESIGN */
.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.checkbox-button input[type="checkbox"] { display: none; } /* Hide the original checkbox */
.checkbox-content {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    background-color: #fff;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    color: #475569;
    font-weight: 500;
}
.checkbox-button:hover .checkbox-content { border-color: #cbd5e1; }
.custom-check {
    width: 20px;
    height: 20px;
    border: 1.5px solid #cbd5e1;
    border-radius: 50%;
    margin-right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
}
.custom-check svg { opacity: 0; transform: scale(0.5); transition: all 0.2s ease; }

/* Checked State */
.checkbox-button input[type="checkbox"]:checked + .checkbox-content {
    border-color: #F59E0B;
    background: #FFFBEB;
    color: #B45309;
    font-weight: 600;
}
.checkbox-button input[type="checkbox"]:checked + .checkbox-content .custom-check {
    background: #F59E0B;
    border-color: #F59E0B;
}
.checkbox-button input[type="checkbox"]:checked + .checkbox-content .custom-check svg {
    opacity: 1;
    transform: scale(1);
}

/* 5. SLIDERS REDESIGN */
.score-badge {
    float: right;
    background: #e2e8f0;
    padding: 2px 8px;
    border-radius: 10px;
    color: #0f172a;
    font-weight: 600;
    font-size: 12px;
}
.slider {
    width: 100%;
    -webkit-appearance: none;
    appearance: none;
    height: 6px;
    border-radius: 4px;
    outline: none;
    margin-top: 8px;
    /* This is the key: a gradient that uses our JS variable */
    background: linear-gradient(to right, #F59E0B 0%, #F59E0B var(--fill-percent), #e2e8f0 var(--fill-percent), #e2e8f0 100%);
}

.slider.orange-slider::-webkit-slider-thumb { background: #F59E0B; }
.slider.orange-slider::-moz-range-thumb { background: #F59E0B; }

.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    cursor: pointer;
    /* Move thumb up slightly to center it on the track */
    margin-top: -7px; 
}

.slider::-moz-range-thumb {
    width: 14px; /* Mozilla needs different dimensions */
    height: 14px;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    cursor: pointer;
}
.slider-labels {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #64748b;
    margin-top: 6px;
}
.level-selector {
    position: relative; /* This is crucial for positioning the arrow */
    display: inline-flex;
    align-items: center;
}

/* The clickable button part */
.level-select-minimal {
    appearance: none;
    background: transparent;
    border: none;
    /* Add padding on the right to make space for the arrow */
    padding: 4px 20px 4px 8px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    cursor: pointer;
    outline: none;
    text-align: right; /* Aligns text to the right */
    transition: color 0.2s;
    min-width: 100px; /* Prevents the button from becoming too small */
}

.level-select-minimal:hover {
    color: #F59E0B;
}

/* Position the arrow on top of the button's padding */
.level-selector .dropdown-caret {
    position: absolute;
    right: 4px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    color: #94a3b8;
    pointer-events: none; /* Allows clicks to go through to the button */
}

/* The floating dropdown menu */
.level-dropdown-menu {
    position: absolute;
    top: calc(100% + 4px); /* Position it right below the button */
    right: 0;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    list-style: none;
    padding: 4px 0;
    margin: 0;
    width: 200px;
    z-index: 100;
    overflow: hidden;
}

/* The individual option items */
.level-dropdown-menu li {
    padding: 10px 16px;
    font-size: 14px;
    color: #1e293b; /* Fix: Black text color */
    cursor: pointer;
    text-align: left; /* Fix: Left aligned */
    border-bottom: 1px solid #f1f5f9; /* Fix: Separator line */
    transition: background-color 0.2s;
}
.level-dropdown-menu li:last-child {
    border-bottom: none; /* Remove line from the last item */
}
.level-dropdown-menu li:hover {
    background-color: #f8fafc; /* Add a subtle hover effect */
}
.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}
.action-icon-btn {
    background: #f1f5f9;
    border: 1px solid #cbd5e1;
    color: #475569;
    border-radius: 6px;
    padding: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}
.action-icon-btn:hover {
    background: #e2e8f0;
    color: #F59E0B; /* Highlights in yellow/orange on hover */
    border-color: #94a3b8;
}
.unified-advanced-modal { display: flex; width: 1100px; height: 85vh; background: #fff; border-radius: 12px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.3); }
.uam-main { flex: 1; display: flex; flex-direction: column; min-width: 0; background: #f8fafc; }
.uam-header { display: flex; justify-content: space-between; padding: 24px 32px 16px; }
.uam-header h2 { margin: 0; font-size: 20px; font-weight: 700; color: #0f172a; display: flex; align-items: center; gap: 8px; }
.uam-subtitle { margin: 4px 0 0 0; font-size: 13px; color: #64748b; font-weight: 500; }
.uam-tabs { display: flex; align-items: center; gap: 4px; padding: 16px 32px; border-bottom: 1px solid #e2e8f0; margin-bottom: 0; background: #fff; }
.uam-tabs button { background: none; border: none; padding: 6px 8px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
.uam-tabs button:hover:not(.tab-disabled) { color: #0f172a; }
.uam-tabs button.active { color: #F59E0B; }
.uam-tabs button svg { width: 14px; height: 14px; }
.uam-tabs button.tab-disabled { opacity: 0.5; cursor: not-allowed; }
.tab-divider { color: #cbd5e1; font-size: 14px; margin: 0 4px; font-weight: 500; }
.uam-content-area { flex: 1; overflow-y: auto; padding: 0 32px 24px; }
.uam-footer { padding: 16px 32px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; background: #fff; }
.uam-sidebar { width: 320px; background: #fff; border-left: 1px solid #e2e8f0; padding: 24px; display: flex; flex-direction: column; gap: 16px; }
.sidebar-title { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px 0; }
.recon-item { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-bottom: 1px dashed #e2e8f0; }
.recon-info { flex: 1; margin: 0 12px; display: flex; flex-direction: column; }
.recon-info span { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
.recon-info strong { font-size: 14px; color: #0f172a; }
.recon-meta { font-size: 12px; color: #64748b; text-align: right; }
.recon-status-box { margin-top: auto; padding: 16px; border-radius: 8px; font-size: 12px; line-height: 1.5; display: flex; gap: 10px; align-items: flex-start; }
.recon-status-box.success { background: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; }
.recon-status-box svg { flex-shrink: 0; margin-top: 2px; color: #10b981; }
.text-yellow svg { color: #F59E0B; } .text-gray svg { color: #94a3b8; }
.reconcile-tab-view { display: flex; flex-direction: column; }
.metric-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; display: flex; flex-direction: column; }
.section-title { font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px; border-bottom: 1px dashed #e2e8f0; padding-bottom: 10px; }
.mt-12 { margin-top: 12px; }
.mt-16 { margin-top: 16px; }

.recon-main-list { display: flex; flex-direction: column; }
.recon-main-row { display: flex; justify-content: space-between; align-items: center; padding: 18px 0; border-bottom: 1px solid #e2e8f0; }
.rm-left { display: flex; align-items: center; gap: 12px; }
.rm-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
.rm-right { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
.rm-val { font-size: 15px; font-weight: 800; color: #0f172a; }
.rm-sub { font-size: 11px; color: #64748b; font-weight: 500; }
.group-select-btn {
    background: #fffbeb !important; 
    color: #b45309 !important; 
    border: 1px solid #fde68a !important;
    border-radius: 6px;
    padding: 6px 24px 6px 12px !important; /* Added padding for pill shape */
    font-size: 12px !important;
    display: inline-flex;
    align-items: center;
    min-width: 130px;
    transition: all 0.2s;
}
.group-select-btn:hover {
    background: #fef3c7 !important;
    border-color: #f59e0b !important;
}

/* Adjust caret position just for the header dropdown */
.header-right .level-selector .dropdown-caret {
    right: 8px; 
}
.text-yellow, .text-yellow svg { color: #F59E0B !important; }
.text-blue, .text-blue svg { color: #3B82F6 !important; }
.text-green, .text-green svg { color: #10B981 !important; }
.text-purple, .text-purple svg { color: #8B5CF6 !important; }
.text-cyan, .text-cyan svg { color: #06B6D4 !important; }
.text-gray, .text-gray svg { color: #94a3b8 !important; }

/* Ensure SVG strokes inherit the parent text class properly */
.icon svg, .recon-icon svg { color: inherit; }
.save-btn-wide { flex: 1; margin-left: 12px; display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 14px; font-weight: 700; padding: 12px; }
</style>